import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';

import { ChatWallpaper } from '@/config/enums/chat-wallpaper';
import { EAttendanceOrder, EServiceOrgs } from '@/config/enums/service';
import useAppSelector from '@/hooks/useAppSelector';
import { AgentPreferences } from '@/models/AgentPreferences';
import {
  getAgentPreferences,
  upsertAgentPreferences,
} from '@/services/api/agentPreferences';
import { RootState } from '@/store';

type AgentPreferencesProviderProps = {
  preferences: AgentPreferences;
  loading: boolean;
  loaded: boolean;
  handleUpsert: (data: AgentPreferences) => Promise<boolean>;
};

const defaultPreferences: AgentPreferences = {
  selected_attendance_filters: [
    EServiceOrgs.TOTAL,
    EServiceOrgs.WAITING,
    EServiceOrgs.ASSUMED,
    EServiceOrgs.GROUP,
  ],
  selected_attendance_order: EAttendanceOrder.LATEST,
  chat_wallpaper: ChatWallpaper.DOODLE_BEIGE,
};

const AgentPreferencesContext = createContext<
  AgentPreferencesProviderProps | undefined
>(undefined);

export const AgentPreferencesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const idDynamicAgent = useAppSelector(
    (state: RootState) => state.payloadAgente.UserPayloadAgent.idDynamicAgent,
  );
  const isAgent = !!idDynamicAgent;

  const [preferences, setPreferences] =
    useState<AgentPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchPreferences = useCallback(async () => {
    if (!isAgent) return;

    setLoading(true);

    const response = await getAgentPreferences();

    if (response.isFail()) {
      toast.error(response.value.message);
      setLoading(false);
      setLoaded(true);
      return;
    }

    setPreferences(response.value);
    setLoading(false);
    setLoaded(true);
  }, [isAgent]);

  const handleUpsert = useCallback(
    async (data: AgentPreferences): Promise<boolean> => {
      if (!isAgent) return false;

      const response = await upsertAgentPreferences(data);

      if (response.isFail()) {
        toast.error(response.value.message);
        return false;
      }

      setPreferences(response.value);

      return true;
    },
    [isAgent],
  );

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const contextValue = useMemo(
    () => ({
      preferences,
      loading,
      loaded,
      handleUpsert,
    }),
    [preferences, loading, loaded, handleUpsert],
  );

  return (
    <AgentPreferencesContext.Provider value={contextValue}>
      {children}
    </AgentPreferencesContext.Provider>
  );
};

export function useAgentPreferences() {
  const context = useContext(AgentPreferencesContext);

  if (!context) {
    throw new Error(
      'useAgentPreferences deve ser usado dentro de um AgentPreferencesProvider',
    );
  }

  return context;
}
